import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { Mail, ScanLine } from "lucide-react"

export interface AskBoxPlaceholderProps {
  /** The unit's own extracted scope wording — the "Originalzitat" the concept
   *  spec's askbox quotes. Null when the statement carries none. */
  scopeVerbatim: string | null
}

/**
 * Geltung "Unklar → Rückfrage", B7's placeholder. Markup is the prototype's
 * `askbox` (`sv2-askbox`/`sv2-ahd`/`sv2-aqt`/`sv2-askrow`). The real
 * Rückfrage flow (TUR-1402, workstream A/D) lands in a later PR — this shows
 * the unresolved quote and a DISABLED send button with a tooltip explaining
 * why, so the control never LOOKS available and then fails. The journey
 * still never dead-ends here: `MC_DECL_V2_ASKBOX_ALTERNATIVE_HINT` names the
 * one alternative that already works today (Anfordern, at the declaration
 * level).
 */
export function AskBoxPlaceholder({ scopeVerbatim }: AskBoxPlaceholderProps) {
  const { translate } = useTranslation()
  return (
    <div data-testid="askbox-placeholder" className="sv2-askbox">
      <div className="sv2-ahd">
        <ScanLine className="sv2-ic" />
        {translate("MC_DECL_V2_ASKBOX_TITLE")}
      </div>
      {scopeVerbatim && (
        <>
          <div className="sv2-aqt">&ldquo;{scopeVerbatim}&rdquo;</div>
          {/* The quote stays in the document's own language; say whose words
              they are so a German sentence in an English screen reads as a
              citation, not as untranslated UI. */}
          <div className="sv2-aqsrc">
            {translate("MC_DECL_V2_ASKBOX_QUOTE_SOURCE")}
          </div>
        </>
      )}
      <div className="sv2-askrow">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span>
                <button type="button" disabled className="sv2-act sv2-primary">
                  <Mail className="sv2-ic" />
                  {translate("MC_DECL_V2_ASKBOX_SEND")}
                </button>
              </span>
            </TooltipTrigger>
            <TooltipContent>
              {translate("MC_DECL_V2_ASKBOX_SEND_DISABLED_HINT")}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <span className="sv2-askn">
          {translate("MC_DECL_V2_ASKBOX_ALTERNATIVE_HINT")}
        </span>
      </div>
    </div>
  )
}
